import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import {
	urlizeFacetObj,
	filterNonInventoryParams,
	findSBParamsMappingKey
} from '../utilities/url-params'; // TODO(Post-flag cleanup - Item 31): Replace with lightweight serializer; legacy file scheduled for removal.
import { setDataLayerTracking } from '../utilities/tracking';
import { getSortOptions } from '../utilities/sortOptions';

/**
 * InvDataRefactored - Modern WIS-only implementation
 *
 * This class is a refactored version of InvData that removes all legacy GVM databus
 * integration while preserving identical public API for backward compatibility.
 *
 * Key changes from legacy InvData:
 * - Removed all GVM databus calls and dual-path logic
 * - Simplified constructor (no useWIS parameter)
 * - Removed _requestResource() method entirely
 * - Uses only modern WIS endpoints
 * - Maintains identical public API contract
 *
 * Related to US1665547: Remove GVM databus from ws-inv-data
 */
export class InvDataRefactored {
	constructor(
		requestWISFacets,
		requestWISInventory,
		requestSpellcheck,
		requestWISVehicle,
		prefs,
		labels,
		parameterMap = {}
	) {
		this.prefs = prefs;
		this.labels = labels;
		this.parameterMap = parameterMap;

		// Initial state
		this.start = 0;
		this.urlParams = {};

		// Data returned from WIS services
		this.facets = {};
		this.appliedFilters = [];
		// window.DDC?.InvData?.inventory is defined by the preload code in /server/inline
		// this is only undefined if the preload FF is off or if the preload feature is not working
		this.inventory = window.DDC?.InvData?.inventory || {};
		this.featuredPromotion = {};
		this.spellcheck = '';
		this.appliedSpellcheck = false;
		this.originalSearchTerm = '';
		this.srpReady = false;
		this.sortOptions = getSortOptions(prefs, labels);
		this.sortBy = '';
		this.filterType = '';
		this.filterSearchEventData = {};

		// Event topics - must remain unchanged for widget compatibility
		this.TOPIC_PREFIX = 'ws-inv-data';
		this.FACETS_TOPIC = `${this.TOPIC_PREFIX}/facets`;
		this.INVENTORY_TOPIC = `${this.TOPIC_PREFIX}/inventory`;
		this.SPELLCHECK_TOPIC = `${this.TOPIC_PREFIX}/spellcheck`;
		this.SORT_BY_TOPIC = `${this.TOPIC_PREFIX}/sortBy`;
		this.CLOSEST_ACCOUNTS_TOPIC = `${this.TOPIC_PREFIX}/closestAccounts`;
		this.TOGGLE_LISTING_VIEW_TOPIC = `${this.TOPIC_PREFIX}/toggleListingView`;

		// Legacy properties removed:
		// - this.WINDOW_ID = 'inventory-data-bus1' (GVM-specific)
		// - this.FACETS_API_URL, this.INVENTORY_API_URL, etc. (GVM endpoints)
		// - this.isWIS (always true in refactored version)
		// - this.fetchers (hysteriFetch for GVM endpoints)
		// - this.SEARCH_API_URL

		this.DEBOUNCE_WAIT = 100;

		this.useOriginalSearchTerm = false;
		this.forceOriginalSearch = false;

		// Geolocation handling
		if (this.prefs.geoLocationEnabled === 'true') {
			// Add New Relic tracking attribute to monitor geoLocationEnabled usage
			setNewRelicCustomAttribute('srpGeoLocationEnabled', 'true');

			// If zip code previously provided by user input, we use that value
			const localStoragePostalCode =
				window.localStorage.getItem('userLocation');

			// Get user location from sessionStorage
			// Note: session.location is set from the akamai header "True-Client-IP"
			// Example value:
			// {"userPostalCode":"90210","userCountry":"US","userState":"CA","userCity":"Beverly Hills","userLatitude":34.0901,"userLongitude":-118.4065}
			const sessionStoragePostalCode = JSON.parse(
				window.sessionStorage.getItem('session.location')
			)?.userPostalCode;

			// Add New Relic tracking attribute to monitor success of client side geolocation data
			setNewRelicCustomAttribute(
				'srpSessionStoragePostalCode',
				sessionStoragePostalCode
			);

			const postalCode =
				localStoragePostalCode || sessionStoragePostalCode;
			this.geoData = {
				geoZip: [postalCode],
				geoRadius: [this.prefs.defaultGeoDistRadius]
			};
			this.geoDataPrefs = {
				geoRadiusValues: this.prefs.geoRadiusValues.replaceAll(' ', ''),
				defaultGeoDistRadius: this.prefs.defaultGeoDistRadius
			};
		}

		// Track listing config ID
		if (prefs['listing.config.id']) {
			setNewRelicCustomAttribute(
				'listingConfigId',
				prefs['listing.config.id']
			);
		}

		// Store WIS request functions passed from Widget.jsx
		this.requestWISFacets = requestWISFacets;
		this.requestWISInventory = requestWISInventory;
		this.requestSpellcheck = requestSpellcheck;
		this.requestWISVehicle = requestWISVehicle;

		// Simplified spellcheck request - WIS only
		this._requestSpellcheck = async () => {
			this.requestSpellcheck(this.urlParams.search.toString()).then(
				(correction) => {
					this.spellcheck = correction;
					this.setLocalStorageForOriginalSearch();
					this._requestFacets('spellcheck');
					this._requestInventory();
				}
			);
		};

		// Simplified facets request - WIS only
		this._requestFacets = window._.debounce((eventType, eventData = {}) => {
			if (this.spellcheck && !this.forceOriginalSearch) {
				this.urlParams.search = [this.spellcheck];
			} else if (this.forceOriginalSearch) {
				this.urlParams.search = [this.originalSearchTerm];
			}
			// eslint-disable-next-line no-unused-vars
			const { start, ...urlParams } = this.urlParams;
			this.requestWISFacets(
				urlParams,
				this.start,
				this.sortBy,
				eventType,
				eventData
			);
		}, this.DEBOUNCE_WAIT);

		// Simplified inventory request - WIS only
		this._requestInventory = window._.debounce((eventType) => {
			const { inventoryFetchPromise } = window?.DDC?.InvData || {};

			if (inventoryFetchPromise) {
				// Handle preload promise if it exists
				inventoryFetchPromise.then((inventoryResponse) => {
					this.inventory = inventoryResponse;
					const {
						pageInfo: { trackingData, ...pageInfo }
					} = inventoryResponse;

					setDataLayerTracking(
						this.appliedFilters,
						this.sortBy,
						{
							pageInfo,
							trackingData
						},
						eventType,
						this.filterSearchEventData
					);
					this.publishEvent(`${this.TOPIC_PREFIX}/inventory`);
					// Clean up preload Promise
					window.DDC.InvData.inventoryFetchPromise = null;
				});
			} else {
				// Standard WIS inventory request
				// eslint-disable-next-line no-unused-vars
				const { sortBy, start, ...urlParams } = this.urlParams;
				this.requestWISInventory(urlParams, this.sortBy, this.start);
			}
		}, this.DEBOUNCE_WAIT);

		this._requestVehicle = async () => {
			this.featuredPromotion = await this.requestWISVehicle();
		};

		this._publishSortBy = window._.debounce(() => {
			this.publishEvent(this.SORT_BY_TOPIC, this.sortBy);
		}, this.DEBOUNCE_WAIT);

		this._loadInitialData();
	}

	// =====================================
	// PUBLIC API METHODS - MUST BE PRESERVED
	// =====================================

	// Event Registration Methods (5 methods)
	registerFacetNotification(callback) {
		this.subscribeEvent(this.FACETS_TOPIC, callback);
	}

	registerInventoryNotification(callback) {
		this.subscribeEvent(this.INVENTORY_TOPIC, callback);
	}

	registerSortByNotification(callback) {
		this.subscribeEvent(this.SORT_BY_TOPIC, callback);
	}

	// TODO: Remove this method when functionality is removed from ddc-js-api
	registerToggleListingView(callback) {
		this.subscribeEvent(this.TOGGLE_LISTING_VIEW_TOPIC, callback);
	}

	// TODO: Remove this method when closest accounts functionality is removed from SRP
	registerClosestAccountsNotification(callback) {
		this.subscribeEvent(this.CLOSEST_ACCOUNTS_TOPIC, callback);
	}

	// Data Getter Methods (14 methods)
	getFacets() {
		return this.facets;
	}

	getSortOptions() {
		return this.sortOptions;
	}

	getInventory() {
		return this.inventory;
	}

	getSpellcheck() {
		return this.spellcheck;
	}

	getPaging() {
		return this.inventory?.pageInfo || null;
	}

	getSortBy() {
		return this.sortBy;
	}

	getAppliedFilters() {
		return this.appliedFilters;
	}

	getUseOriginalSearchTerm() {
		return this.useOriginalSearchTerm;
	}

	getForceOriginalSearch() {
		return this.forceOriginalSearch;
	}

	getGeoData() {
		return this.geoData;
	}

	getGeoDataPrefs() {
		return this.geoDataPrefs;
	}

	// Legacy Support Method - Always returns true in refactored version
	/* eslint-disable class-methods-use-this */
	getIsWIS() {
		// In the refactored version, we're always using WIS
		return true;
	}
	/* eslint-enable class-methods-use-this */

	// State Mutation Methods (7 methods)
	replaceSelections(urlParams, sortBy, start, updateBrowserHistory = true) {
		this.urlParams = urlParams;
		this.start = start;
		this.sortBy = sortBy;

		if (urlParams.geoZip) {
			this.urlParams.geoRadius = ['0'];
		}

		if (updateBrowserHistory) {
			this._updateBrowserHistory();
		}

		this._requestFacets();
		this._requestInventory();
	}

	clearForceOriginalSearch() {
		if (this.forceOriginalSearch) {
			window.localStorage.removeItem('forceOriginalSearch');
			this.forceOriginalSearch = false;
		}
	}

	clearOriginalSearchTerm() {
		if (this.originalSearchTerm) {
			window.localStorage.removeItem('originalSearchTerm');
			this.originalSearchTerm = '';
		}
	}

	clearFilters() {
		const urlParams = filterNonInventoryParams(
			this.urlParams,
			this.appliedFilters
		);

		this.clearSpellcheckMsg();
		this.clearForceOriginalSearch();
		this.clearOriginalSearchTerm();
		this.replaceSelections(urlParams, this.sortBy, 0 /* start */, true);
	}

	// clear the spellcheck so that it will allow to clear filters and replace selections
	clearSpellcheckMsg() {
		this.spellcheck = '';
		this.useOriginalSearchTerm = false;
		this.publishEvent(`${this.TOPIC_PREFIX}/clearSpellcheckMsg`);
	}

	setSortBy(value = '') {
		this.sortBy = value.split();

		this._updateBrowserHistory();
		this._requestInventory('sort');
		this._publishSortBy();
	}

	setLocalStorageForOriginalSearch() {
		if (Object.keys(this.urlParams).includes('search')) {
			window.localStorage.setItem(
				'originalSearchTerm',
				this.urlParams.search.toString()
			);
			setNewRelicCustomAttribute(
				'originalSearchTerm',
				this.originalSearchTerm
			);
			setNewRelicCustomAttribute('CorrectedSearchTerm', this.spellcheck);
			this.useOriginalSearchTerm = true;
		}
	}

	setLocalStorageForForceSearch() {
		window.localStorage.setItem('forceOriginalSearch', 'true');
		this.forceOriginalSearch = window.localStorage.getItem(
			'forceOriginalSearch'
		);
	}

	setOriginalSearchTerm() {
		this.useOriginalSearchTerm = true;
		this.spellcheck = '';
		if (this.originalSearchTerm) {
			setNewRelicCustomAttribute(
				'selectedOriginalSearchTerm',
				this.originalSearchTerm
			);
		}
		this.setLocalStorageForForceSearch();
		this.clearSpellcheckMsg();
		this._requestFacets();
		this._requestInventory();
	}

	spellcheckSearchTerm(event, facetInfo) {
		window.DDC.InvData.clearFilters();
		if (facetInfo.search) {
			const searchValue = [];
			searchValue.push(facetInfo.search);
			this.urlParams.search = searchValue;
			window.history.pushState(
				{
					urlParams: this.urlParams
				},
				'',
				`${window.location.origin}${
					window.location.pathname
				}${this.getQueryString()}`
			);
		}
		this._requestSpellcheck();

		// Persisting geoZip and geoRadius
		const geoData = this.getAppliedFilters()
			.filter((key) => key.id.includes('geo'))
			.reduce((obj, item) => {
				return {
					...obj,
					[item.id]: item.values[0].value
				};
			}, {});

		const newFacetInfo = {
			...geoData,
			...facetInfo
		};

		Object.keys(newFacetInfo).forEach((name) => {
			window.DDC.InvData.setFilterSelections({
				filterName: name,
				filterValues: [newFacetInfo[name]],
				filterValue: newFacetInfo[name],
				element: event.target
			});
		});
	}

	/**
	 *
	 * @param {string} filterName Name of filter (ex: 'make')
	 * @param {string[]} filterValues Values of filter (ex: ['BMW', 'Toyota'])
	 * @param {string} filterValue most recently selected value of the filter, used for tracking
	 * @param {string} filterTitleText Optional Title label as the user sees it (ex: 'Fuel Type')
	 * @param {boolean} [checked=true] if filter is being added (true) or removed (false)
	 * @param {boolean} [isSlider=false] if filter is slider
	 * @param {boolean} [isBreadcrumb=false] if filter is breadcrumb
	 * @param element {object} element interacted with
	 */
	setFilterSelections({
		filterName,
		filterValues,
		filterValue,
		element,
		isSelected = true,
		isSlider = false,
		isChip = false,
		filterTitleText = ''
	}) {
		this.updateUrlParams({ filterName, filterValues });
		this.start = 0;
		this.filterType = filterName;
		this.filterSearchEventData = {
			clickedFacetName: filterName,
			clickedFacetValue: filterValue,
			clickedFacetTitleText: filterTitleText,
			checked: isSelected,
			isSlider,
			isBreadcrumb: isChip,
			element
		};

		if (filterName === 'geoZip') {
			this.geoData.geoZip = filterValue;
		}

		this._updateBrowserHistory();

		// clear the spellcheck so that it will allow to clear filters
		if (!Object.keys(this.urlParams).includes('search')) {
			this.clearSpellcheckMsg();
			this.clearForceOriginalSearch();
			this.clearOriginalSearchTerm();
		}
		const eventType = filterName === 'search' ? 'search' : 'facet';
		this._requestFacets(eventType, this.filterSearchEventData);
		this._requestInventory(eventType);
	}

	updateUrlParams({ filterName, filterValues }) {
		// On redirect from a missing VDP, the corresponding make and model are automatically applied as filters
		// based on the the vdpUrl param. Here, we replace the vdpUrl param with params for the filters.
		if (Object.hasOwn(this.urlParams, 'vdpUrl')) {
			this.appliedFilters.forEach(({ id, values }) => {
				if (!Object.hasOwn(this.urlParams, id)) {
					this.urlParams[id] = [values[0]?.value];
				}
			});
			delete this.urlParams.vdpUrl;
		}

		// find a SB param key if it exists to add or remove facet
		const SBParamKey = findSBParamsMappingKey(filterName);
		const hasLegacyParam =
			SBParamKey.length > 0 &&
			Object.prototype.hasOwnProperty.call(this.urlParams, SBParamKey);

		if (!filterValues || !filterValues.length) {
			// Handle removing deprecated legacy `incentiveId` param due to conditional applied filter logic for `incentiveIds`
			if (
				filterName === 'incentiveIds' &&
				!this.urlParams.incentiveIds &&
				this.urlParams.incentiveId
			) {
				delete this.urlParams.incentiveId;
			} else {
				// Handle removing SB param from the url
				if (hasLegacyParam) {
					delete this.urlParams[SBParamKey];
				}
				delete this.urlParams[filterName];
			}
		} else {
			// Handle removing only SB param from the url with other facet choices selected
			if (
				hasLegacyParam &&
				!filterValues.includes(this.urlParams[SBParamKey][0])
			) {
				delete this.urlParams[SBParamKey];
			}
			this.urlParams[filterName] = filterValues;
		}
	}

	setStart(index, updateBrowserHistory = true) {
		this.start = index.toString().split();

		if (updateBrowserHistory) {
			this._updateBrowserHistory();
		}

		this._requestFacets();
		this._requestInventory();
	}

	// Utility Methods (5 methods)
	getQueryStringObj(overrides = {}) {
		const urlParams = { ...this.urlParams };

		if (this.sortBy && !this.sortBy.includes('')) {
			urlParams.sortBy = this.sortBy;
		}
		if (this.start !== 0) {
			urlParams.start = this.start;
		}

		return {
			...urlParams,
			...overrides
		};
	}

	getQueryString(overrides = {}) {
		return urlizeFacetObj(this.getQueryStringObj(overrides));
	}

	// =====================================
	// PRIVATE METHODS
	// =====================================

	_updateBrowserHistory() {
		window.history.pushState(
			{
				urlParams: this.urlParams,
				sortBy: this.sortBy,
				start: this.start
			},
			'',
			`${window.location.origin}${
				window.location.pathname
			}${this.getQueryString()}`
		);
	}

	/* eslint-disable class-methods-use-this */
	publishEvent(topic, ...params) {
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.publish(topic, ...params);
		}
	}

	subscribeEvent(topic, callback) {
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.subscribe(topic, callback);
		}
	}
	/* eslint-enable class-methods-use-this */

	_loadInitialData() {
		setNewRelicCustomAttribute('isSRP', 'true');
		setNewRelicCustomAttribute('ws-inv-data-refactored', 'true'); // Track usage of refactored class

		const { sortBy = '', start = 0, ...urlParams } = this.parameterMap;

		const hasGeoParams = this.geoData
			? Object.keys(this.geoData).every((key) =>
					Object.keys(urlParams).includes(key)
			  )
			: null;
		if (this.geoData && !hasGeoParams) {
			this.urlParams = { ...urlParams, ...this.geoData };

			// Populating URL params on page load
			window.history.pushState(
				{
					urlParams: this.urlParams
				},
				'',
				`${window.location.origin}${
					window.location.pathname
				}${this.getQueryString()}`
			);
		} else {
			this.urlParams = urlParams;
		}
		this.srpReady = true;

		this.sortBy = sortBy;
		this.start = start;

		this.originalSearchTerm =
			window.localStorage.getItem('originalSearchTerm');
		this.forceOriginalSearch = window.localStorage.getItem(
			'forceOriginalSearch'
		);

		const searchInUrlParams = Object.keys(this.urlParams).includes(
			'search'
		);
		const searchValueInUrlParams = searchInUrlParams
			? this.urlParams.search.toString()
			: '';

		if (
			searchInUrlParams &&
			searchValueInUrlParams !== this.originalSearchTerm
		) {
			this.clearForceOriginalSearch();
			this.useOriginalSearchTerm = false;
		} else if (
			searchInUrlParams &&
			searchValueInUrlParams === this.originalSearchTerm
		) {
			this.useOriginalSearchTerm = false;
		} else if (searchInUrlParams) {
			this.useOriginalSearchTerm = true;
		} else {
			this.useOriginalSearchTerm = false;
			this.clearOriginalSearchTerm();
		}

		// check to see if there is search param in the url, it will call request spellcheck endpoint
		if (searchInUrlParams) {
			this._requestSpellcheck();
		} else {
			this._requestFacets();
			this._requestInventory();
			this._requestVehicle();
		}
		this._publishSortBy();
	}
}
